import { useContext, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { ReactReduxContext, type ReactReduxContextValue } from "react-redux";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import type { SubscriptionOptions } from "@reduxjs/toolkit/query";
import { isFeatureDisabled } from "../store/api/featureDisabled";
import { sharepointApi, type SharePointStatus } from "../store/api/sharepointApi";

/**
 * connected  SharePoint is available here and the organisation has connected it
 * off        known not to be: switched off for the instance, not configured,
 *            not connected, or the status failed
 * pending    not known yet
 */
export type SharePointPresence = "connected" | "off" | "pending";

/** The one rule for "is the Document Centre a thing in this instance". */
export function sharePointPresenceOf(
  status: SharePointStatus | undefined,
  failed: boolean,
): SharePointPresence {
  // Switched off for the instance, or not for this person's role, is "off"
  // before anything else is looked at. Only a `false` says so: an answer
  // without the field is from a server that predates it (see SharePointStatus).
  if (status) {
    return status.available !== false &&
      status.canView !== false &&
      status.configured &&
      status.org.connected
      ? "connected"
      : "off";
  }
  return failed ? "off" : "pending";
}

/**
 * Whether this person's role lets them change candidate documents. Only a
 * `false` says no, as with `canView`.
 */
export function mayManageDocuments(status: SharePointStatus | undefined): boolean {
  return status?.canManageDocuments !== false;
}

type AnyStore = ReactReduxContextValue["store"];

const REREAD_ON_RETURN: SubscriptionOptions = { refetchOnFocus: true, refetchOnReconnect: true };
const NO_REREAD: SubscriptionOptions = { refetchOnFocus: false, refetchOnReconnect: false };

const noSubscription = () => () => {};

/**
 * The store, when it is one that can answer: it has to carry the API slice.
 * A store without it, like no store at all, is "no answer".
 */
export function answeringStore(store: AnyStore | undefined | null): AnyStore | null {
  if (!store) return null;
  try {
    const state = store.getState() as Record<string, unknown> | null;
    return state && sharepointApi.reducerPath in state ? store : null;
  } catch {
    return null;
  }
}

/**
 * Whether a failed read of the status is worth asking for again: the server
 * could not be reached, was failing, or was busy. A refusal is an ANSWER and
 * is never asked again: the feature switched off, and equally a 403 (a client
 * portal user is refused the status for good), a 404 or a 400. Those cost one
 * request per page load and no more.
 */
export function statusFailureIsTemporary(error: unknown): boolean {
  if (!error || typeof error !== "object" || isFeatureDisabled(error)) return false;
  const { status, originalStatus } = error as { status?: unknown; originalStatus?: unknown };
  const passing = (code: unknown) => typeof code === "number" && (code >= 500 || code === 429);
  if (typeof status === "number") return passing(status);
  // The server answered with something that was not JSON: a proxy's error page.
  if (status === "PARSING_ERROR") return passing(originalStatus);
  return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
}

/**
 * Whether SharePoint is connected for this instance, for the places that only
 * show the Document Centre when it is: the sidebar item, the header search and
 * the Documents tab of a candidate's profile.
 *
 * `connected` is false until it is known to be true, so the menu item appears a
 * beat late for an instance that has it rather than flashing in and out for one
 * that does not. `settled` says the answer is in, either way. With `enabled`
 * false nothing is fetched and nothing is settled.
 *
 * It reads the same cached /sharepoint/status entry the Document Centre and the
 * settings card use, and keeps it subscribed, so connecting or disconnecting
 * (which invalidate that entry) updates the menu without a reload.
 *
 * Written against the store directly instead of with the generated query hook,
 * so that it can never be the reason the shell fails to render or navigate.
 * The sidebar, the header search and the route guard are mounted in places
 * with no store, and with a store that does not carry the API slice. A
 * generated hook throws in both. This one asks for nothing there and answers
 * "not connected", which is also the right default. A dispatch that throws
 * leaves it at the same answer.
 *
 * A read that failed because the server could not be reached, was failing or
 * was busy is "not connected" for now, and is read again when the person
 * comes back to the tab or the network comes back, so the menu item returns
 * after an outage without a reload. Any answer, a refusal included, is final
 * until the page is loaded again.
 */
export function useSharePointConnected(
  instanceId: string | null | undefined,
  enabled = true,
): { connected: boolean; settled: boolean; canManageDocuments: boolean } {
  const store = answeringStore(useContext(ReactReduxContext)?.store);
  const active = Boolean(store && instanceId && enabled);

  // The hook's own subscription to the status, kept so its options can change.
  const subscription = useRef<{
    unsubscribe: () => void;
    updateSubscriptionOptions: (options: SubscriptionOptions) => void;
  } | null>(null);

  useEffect(() => {
    if (!store || !instanceId || !enabled) return undefined;
    try {
      const dispatch = store.dispatch as ThunkDispatch<unknown, unknown, UnknownAction>;
      const held = dispatch(
        sharepointApi.endpoints.getSharePointStatus.initiate(
          { instanceId },
          // Not read again on its own to begin with: see `failedForNow` below.
          { subscriptionOptions: NO_REREAD },
        ),
      );
      subscription.current = held;
      return () => {
        subscription.current = null;
        held.unsubscribe();
      };
    } catch {
      // A store that cannot run the request leaves the answer at "not connected".
      return undefined;
    }
  }, [store, instanceId, enabled]);

  const select = useMemo(
    () => sharepointApi.endpoints.getSharePointStatus.select({ instanceId: instanceId ?? "" }),
    [instanceId],
  );

  // A string, so the snapshot is the same value between renders unless the
  // answer itself changed.
  const presence = useSyncExternalStore<SharePointPresence>(
    store && active ? store.subscribe : noSubscription,
    () => {
      // Not asked is not the same as "no": a caller that has switched this off
      // for now (a closed panel) must not read it as a decision.
      if (store && instanceId && !enabled) return "pending";
      if (!store || !active) return "off";
      try {
        const entry = select(store.getState() as Parameters<typeof select>[0]);
        return sharePointPresenceOf(entry.data, entry.isError);
      } catch {
        return "off";
      }
    },
  );

  // The last read failed for a passing reason and there is no earlier answer:
  // the Document Centre is hidden, and it should come back by itself. While
  // that is so, and only then, the status is read again when the person comes
  // back to the tab or the network comes back.
  //
  // No timer and no count of this hook's own. The API layer already retries a
  // dropped connection, and a second mechanism on top of it is how a failed
  // read once turned into a request every two seconds. And only while failed:
  // an answer is an answer, whether it is "connected", "switched off for this
  // instance" or a refusal, and re-reading it on every return to the tab would
  // be traffic from every user of every instance, most of which do not have
  // the feature at all.
  const failedForNow = useSyncExternalStore<boolean>(
    store && active ? store.subscribe : noSubscription,
    () => {
      if (!store || !active) return false;
      try {
        const entry = select(store.getState() as Parameters<typeof select>[0]);
        return entry.status === "rejected" && !entry.data && statusFailureIsTemporary(entry.error);
      } catch {
        return false;
      }
    },
  );
  useEffect(() => {
    subscription.current?.updateSubscriptionOptions(failedForNow ? REREAD_ON_RETURN : NO_REREAD);
  }, [failedForNow]);

  // Page-level only (the delete dialog's checkbox): never true where the
  // Document Centre itself is not.
  const mayManage = useSyncExternalStore<boolean>(
    store && active ? store.subscribe : noSubscription,
    () => {
      if (!store || !active) return false;
      try {
        return mayManageDocuments(select(store.getState() as Parameters<typeof select>[0]).data);
      } catch {
        return false;
      }
    },
  );

  return {
    connected: presence === "connected",
    settled: presence !== "pending",
    canManageDocuments: presence === "connected" && mayManage,
  };
}
